import { FormControl, Grid } from '@mui/material';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Controller, ControllerRenderProps } from 'react-hook-form';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { TypeCard } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/pages/Type/TypeCard';
import { usePresentationFormContext } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/PresentationFormContext';
import {
  PresentationsTabsPath,
  Presentation,
  ReportTypeStep,
  ReportTypeFormValue
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/types';
import { checkCreditBalance } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/service/api';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SearchTextField } from 'src/components/BeaconV2Components/TableSearch';
import { useAppSelector } from 'src/utils/Hooks';
import WarningModal from 'src/components/Modals/WarningModal';

export type PresentationType = {
  id: string;
  label: string;
  description: string;
  tags?: string[];
  totalSections?: number;
  totalSlides?: number;
  isFromAPI?: boolean;
  updatedTime?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'DRAFT' | 'COMING_SOON';
};

const types: PresentationType[] = [
  {
    id: 'FINAL',
    label: 'Market Share Overview',
    description: 'Insights on market share, competitors, and growth trends.',
    tags: ['Sales', 'Market Share', 'Competitors'],
    totalSections: 3,
    totalSlides: 42
  },
  {
    id: 'behavior-analysis',
    label: 'Shopper Behavior Analysis',
    description: 'Insights on shopper trends, preferences, and buying patterns.',
    status: 'COMING_SOON'
  },
  {
    id: 'new-product-deep-dive',
    label: 'New Product Deep Dive',
    description: 'Insights on product features, performance, and market fit.',
    status: 'COMING_SOON'
  },
  {
    id: 'household-penetration',
    label: 'Household Penetration',
    description: 'Insights on brand reach, consumer adoption, and market presence. ',
    status: 'COMING_SOON'
  },
  {
    id: 'pricing-promotions',
    label: 'Pricing & Promotions',
    description: 'Insights on pricing strategies, discounts, and promotional trends.',
    status: 'COMING_SOON'
  }
];

// Helper function to transform API presentations to PresentationType
const transformAPIPresentation = (apiPresentation: Presentation): PresentationType => ({
  id: apiPresentation.presentationId,
  label: apiPresentation.presentationName || 'Untitled Presentation',
  description: apiPresentation.description || 'No description available',
  totalSections: 3, // Default value since this field is not in the new API
  tags: apiPresentation.tags?.filter((tag) => tag && tag.length > 0) ?? [],
  totalSlides: apiPresentation.totalSlides || 0,
  isFromAPI: true,
  updatedTime: apiPresentation.updatedTime
});

type WarningVariant = 'noActiveContracts' | 'noCreditBalance';

export const PresentationsTypePage = () => {
  const {
    type: { control },
    presentations,
    isPresentationsLoading,
    presentationsError
  } = usePresentationFormContext();

  const [searchQuery, setSearchQuery] = useState('');
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [warningVariant, setWarningVariant] = useState<WarningVariant>('noActiveContracts');
  const [isCheckingBalance, setIsCheckingBalance] = useState(false);
  const { beaconNavigator } = useBeaconNavigator();
  const divisionId = useAppSelector((state) => state.user?.session?.divisionId);

  const handleCreateClick = useCallback(
    async (
      type: ReportTypeFormValue,
      field: ControllerRenderProps<ReportTypeStep, 'reportType'>
    ) => {
      setIsCheckingBalance(true);
      try {
        const balanceResponse = await checkCreditBalance(divisionId);

        if (!balanceResponse.canProceed) {
          if (!balanceResponse.hasActiveContract) {
            setWarningVariant('noActiveContracts');
          } else {
            setWarningVariant('noCreditBalance');
          }
          setIsWarningOpen(true);
          return;
        }

        field.onChange(type);
        beaconNavigator.setPathname(PresentationsTabsPath.selections).go();
      } catch {
        field.onChange(type);
        beaconNavigator.setPathname(PresentationsTabsPath.selections).go();
      } finally {
        setIsCheckingBalance(false);
      }
    },
    [divisionId, beaconNavigator]
  );

  const combinedPresentations = useMemo(() => {
    // Transform and sort API presentations by updatedTime (most recent first)
    const apiPresentations = presentations
      .map(transformAPIPresentation)
      .sort((a, b) => {
        if (!a.updatedTime && !b.updatedTime) return 0;
        if (!a.updatedTime) return 1;
        if (!b.updatedTime) return -1;
        return new Date(b.updatedTime).getTime() - new Date(a.updatedTime).getTime();
      });

    // Get the first hardcoded presentation and the rest
    const [firstHardcoded, ...remainingHardcoded] = types;

    // Combine: first hardcoded, then sorted API presentations, then remaining hardcoded
    const combined = [firstHardcoded, ...apiPresentations, ...remainingHardcoded];
    return combined;
  }, [presentations]);

  const filteredTypes = combinedPresentations.filter(
    (type) =>
      type.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      type.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isStacklineUser = useAppSelector((state) => state.user.config.isStacklineUser);
  const disabled = false; // Quota system removed

  return (
    <Flex flexDirection="column" gap={32}>
      <Flex flexDirection="column">
        <Text variant="h4" lineHeight="28px">
          Presentation Type
        </Text>
        <Flex marginTop="8px" fontSize={16} lineHeight="24px">
          Choose the type of presentation that you would like to build.
        </Flex>
        {/* Display presentations loading state and data info */}
        {isPresentationsLoading && (
          <Flex marginTop="4px">
            <Text variant="body2" opacity={70}>
              Loading presentations data...
            </Text>
          </Flex>
        )}
        {presentationsError && (
          <Flex marginTop="4px">
            <Text variant="body2">Error loading presentations: {presentationsError.message}</Text>
          </Flex>
        )}
      </Flex>
      <Flex flexDirection="column" gap={32}>
        <SearchTextField
          placeholder="Search presentations"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Controller
          control={control}
          name="reportType"
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error}>
              <Grid container spacing={2}>
                {filteredTypes.map((type) => (
                  <Grid item xs={4} key={type.id}>
                    <TypeCard
                      disabled={disabled || isCheckingBalance}
                      type={type}
                      isSelected={field.value.id === type.id}
                      onSelect={(selectedType) => handleCreateClick(selectedType, field)}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormControl>
          )}
        />
      </Flex>
      <WarningModal isOpen={isWarningOpen} setIsOpen={setIsWarningOpen} variant={warningVariant} />
    </Flex>
  );
};
